// #959 — admin Crons overview. Lists every registered APScheduler
// job with its periodicity, next-run-at, and the most-recent
// cron_runs row (status / duration / metadata).
//
// Click on a row to drill into the per-cron detail view. Only the
// News Crawler row has a detail view today; the others render
// non-clickable. Future stories will add more detail views and the
// click behaviour generalises (driven by JOB_DETAIL_ROUTES below).
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import {
  userSubPageStyles, fmtDateTime, adminFetch,
} from './userSubPageStyles';

const VERSION = 'v1.0.0';

// Map a cron's job_id to its detail-view route. Jobs not in this map
// render as non-clickable rows (no detail view available yet).
const JOB_DETAIL_ROUTES = {
  refresh_cached_briefings: '/admin/crons/news-crawler',
};

function statusColor(status) {
  if (status === 'success') return '#27AE60';
  if (status === 'failed')  return '#C0392B';
  if (status === 'running') return '#E8845A';
  return '#aaa';
}

function fmtDuration(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s % 60);
  return `${m}m ${rs}s`;
}

function MetadataChip({ meta }) {
  if (!meta || typeof meta !== 'object') return null;
  const keys = Object.keys(meta);
  if (keys.length === 0) return null;
  // Short pill — first 2 keys; full payload on hover.
  const preview = keys.slice(0, 2)
    .map(k => `${k}: ${meta[k]}`)
    .join(', ');
  return (
    <span
      title={JSON.stringify(meta, null, 2)}
      style={{
        display: 'inline-block',
        padding: '2px 8px', borderRadius: 10,
        background: '#FAF8F5', color: '#666',
        fontSize: '0.75rem', fontFamily: 'ui-monospace, monospace',
        cursor: 'help',
      }}
    >
      {preview}{keys.length > 2 ? ', …' : ''}
    </span>
  );
}

export default function AdminCrons() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminFetch('/api/admin/crons');
        if (cancelled) return;
        setJobs(r?.jobs || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AdminLayout title="Crons">
      <style>{userSubPageStyles}</style>
      <style>{`
        .crons-badge {
          display: inline-block; padding: 2px 8px; border-radius: 10px;
          font-size: 0.72rem; font-weight: 600; text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .crons-row-clickable { cursor: pointer; }
        .crons-row-clickable:hover { background: #FFF9F5; }
        .crons-detail-hint {
          color: #E8845A; font-size: 0.78rem;
        }
      `}</style>

      <div className="page-header">
        <div className="page-title">
          Crons<span className="ver">{VERSION}</span>
        </div>
        <div className="page-subtitle">
          Scheduled jobs running inside the app container · last-run + next-run + status
        </div>
      </div>

      {loading && <p className="loading-state">Loading…</p>}
      {error && <p className="error-state">Error: {error}</p>}

      {jobs && jobs.length === 0 && (
        <div className="empty-state">
          No scheduled jobs registered. Scheduler not running?
        </div>
      )}

      {jobs && jobs.length > 0 && (
        <div className="data-grid">
          <table className="data-table">
            <thead>
              <tr>
                <th>Job</th>
                <th>Periodicity</th>
                <th>Last run</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Next run</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => {
                const route = JOB_DETAIL_ROUTES[j.job_id];
                const clickable = !!route;
                const lr = j.last_run;
                const status = lr?.status || 'never';
                return (
                  <tr
                    key={j.job_id}
                    className={clickable ? 'crons-row-clickable' : ''}
                    onClick={() => { if (clickable) navigate(route); }}
                  >
                    <td>
                      <div style={{ fontWeight: 600 }}>{j.display_name}</div>
                      <div style={{
                        color: '#888', fontSize: '0.75rem',
                        fontFamily: 'ui-monospace, monospace',
                      }}>
                        {j.job_id}
                      </div>
                    </td>
                    <td style={{ color: '#666', fontSize: '0.85rem' }}>
                      {j.periodicity}
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {lr ? fmtDateTime(lr.started_at) : <em>never</em>}
                      {lr?.metadata && (
                        <div style={{ marginTop: 3 }}>
                          <MetadataChip meta={lr.metadata} />
                        </div>
                      )}
                    </td>
                    <td style={{ color: '#666', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {fmtDuration(lr?.duration_ms)}
                    </td>
                    <td>
                      <span
                        className="crons-badge"
                        style={{
                          background: status === 'success' ? '#E5F8EB'
                                    : status === 'failed'  ? '#FCE7E4'
                                    : status === 'running' ? '#FFF1E8'
                                    : '#EEEEEE',
                          color: statusColor(status),
                        }}
                      >
                        {status}
                      </span>
                      {lr?.error_message && (
                        <div
                          title={lr.error_message}
                          style={{
                            color: '#C0392B', fontSize: '0.72rem',
                            fontFamily: 'ui-monospace, monospace',
                            marginTop: 3, maxWidth: 240,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lr.error_message}
                        </div>
                      )}
                    </td>
                    <td style={{ color: '#aaa', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                      {fmtDateTime(j.next_run_at)}
                    </td>
                    <td>
                      {clickable ? (
                        <span className="crons-detail-hint">view →</span>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: '0.82rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
